import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Button, Card } from 'antd';

import { getFilterOptions } from '../../../helpers/filter';
import { hasPermission } from '../../../helpers/user';

import { MAIN_GROUP_TYPE } from '../../../constants/group';
import { ANIMAL_ERRORS, BLANK_FILTER_TEXT } from '../../../constants/livestock';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';
import { TABLE_CONFIG_KEYS } from '../../../constants/table-configurations';

import { useGroupLivestockColumns } from '../../../hooks/useGroupLivestockColumns';
import { useModal } from '../../../hooks/useModal';

import { addAnimalsToGroup, removeAnimalsFromGroup } from '../../../redux/actions/label';
import { setSortedData } from '../../../redux/reducers/animal';
import { setSelectedFilters } from '../../../redux/reducers/filters';

import AppModal from '../../../components/AppModal';
import AppTable from '../../../components/AppTable';
import { errorToastHandler, successToastHandler } from '../../../components/action_notifier';

function GroupLivestock({ groupGeofenceId, data, loading, onUpdateLivestock, handleSearchChange, mainGroupType }) {
  const dispatch = useDispatch();

  const { id: groupId } = useParams();

  const { selectedFilters } = useSelector(state => state.filters);

  const [filteredInfo, setFilteredInfo] = useState({});
  const [isAddConfirmationOpen, setIsAddConfirmationOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] = useState(false);
  const [selectedLivestockIds, setSelectedLivestockIds] = useState([]);
  const [animalPhysicalGroup, setAnimalPhysicalGroup] = useState('');

  const [isAnimalInPhysGroup, openConfirmPhysGroupModal, closeConfirmPhysGroupModal] = useModal();
  const [isReassignConfirmationOpen, openReassignConfirmationModal, closeReassignConfirmationModal] = useModal();

  const [usedGroupMessage, setUsedGroupMessage] = useState('');
  const [usedGroups, setUsedGroups] = useState([]);

  const user = useSelector(state => state.user);

  const labelData = useSelector(state => state.label);

  const [dataSource, setDataSource] = useState(data);
  const [updatedDataSource, setUpdatedDataSource] = useState(undefined);

  useEffect(() => {
    return () => {
      dispatch(setSelectedFilters({}));
    };
  }, []);

  useEffect(() => {
    setDataSource([...data]); 
  }, [data]);

  useEffect(() => {
    if (selectedRecord?.id) {
      selectedRecord.isIncluded.value === 1
        ? hasAnimalDifferentGeofence(selectedRecord.id)
        : toggleRemoveConfirmModal();
    }
  }, [selectedRecord]);

  const filterOptions = useMemo(
    () => ({
      breedingStatuses: getFilterOptions(data, 'breeding_status'),
      breeds: getFilterOptions(data, 'breed', 'display_name'),
      geofences: [
        ...new Set(data?.map(l => (l.assigned_geofences.length ? l.assigned_geofences.at(0) : BLANK_FILTER_TEXT)))
      ].map(geofence => ({ text: geofence, value: geofence })),
      sex: getFilterOptions(data, 'sex'),
      types: getFilterOptions(data, 'stock_type')
    }),
    [data]
  );

  const toggleAddConfirmModal = useCallback(() => {
    setIsAddConfirmationOpen(prevState => !prevState);
  }, [isAddConfirmationOpen]);

  const toggleRemoveConfirmModal = useCallback(() => {
    setIsRemoveConfirmationOpen(prevState => !prevState);
  }, [isRemoveConfirmationOpen]);

  const handleTableChange = useCallback(
    (pagination, filters, sorter, { currentDataSource }) => {
      setFilteredInfo(filters);
      dispatch(setSelectedFilters(filters));
      dispatch(setSortedData(currentDataSource));
    },
    [setFilteredInfo, setSortedData, dispatch]
  );

  const clearFilters = useCallback(() => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  }, [setFilteredInfo, dispatch]);

  const handleRemoveFromGroup = useCallback(async () => {
    if (!selectedRecord?.id) return;

    await dispatch(removeAnimalsFromGroup({ id: groupId, data: { animal_ids: [selectedRecord?.id] } })).unwrap();

    onUpdateLivestock();

    toggleRemoveConfirmModal();
    successToastHandler('Removed from the group successfully!');
  }, [onUpdateLivestock, toggleRemoveConfirmModal]);

  const handleAddAnimalsToGroup = useCallback(async () => {
    if (!selectedRecord?.id) return;

    // Animal can't be added to a group that doesn't have geofence
    await dispatch(addAnimalsToGroup({ id: groupId, data: { animal_ids: [selectedRecord?.id] } })).unwrap();

    onUpdateLivestock();

    setIsAddConfirmationOpen(false);
    closeReassignConfirmationModal();
    setAnimalPhysicalGroup('');
    successToastHandler(messages.UPDATED);
  }, [selectedRecord, onUpdateLivestock, setIsAddConfirmationOpen]);

  const handleChangeTableData = updatedData => {
    const record = updatedData.at(0);
    setSelectedRecord(record);
  };

  function hasAnimalDifferentGeofence(id) {
    let isAnimalWithDifferentGeofenceId = false;

    const animal = data?.find(i => i.id === id);
    const sameGeofence = animal?.geofences?.find(i => i.id === groupGeofenceId);
    const isAnimalGeofenceMaster = animal?.geofences[0]?.is_master ?? false;

    const animalPhysicalGroup = animal?.labels?.find(label => label.main_type === MAIN_GROUP_TYPE.PHYSICAL);

    if (animalPhysicalGroup) {
      setAnimalPhysicalGroup(animalPhysicalGroup.name);
      openReassignConfirmationModal();
      return;
    }

    if (!sameGeofence && !isAnimalGeofenceMaster) {
      isAnimalWithDifferentGeofenceId = true;
    }

    isAnimalWithDifferentGeofenceId ? toggleAddConfirmModal() : handleAddAnimalsToGroup(id);
  }

  const removeLivestockFromGroup = async () => {
    if (selectedLivestockIds?.length) {
      await dispatch(removeAnimalsFromGroup({ id: groupId, data: { animal_ids: selectedLivestockIds } })).unwrap();
    }
    onUpdateLivestock();

    successToastHandler('Removed from the group successfully!');
  };

  const addLivestockToGroup = async () => {
    const { label: currentLabel, data: groups } = labelData;

    // if current group is physical need to check if animal(s) already assigned to other physical group
    if (currentLabel && currentLabel.main_type === MAIN_GROUP_TYPE.PHYSICAL) {
      const physicalGroups = groups.filter(group => group.main_type === 'physical' && group.id !== currentLabel.id);
      let usedGroups = [];
      selectedLivestockIds.forEach(livestockId => {
        // find groups where animal is assigned
        const filteredGroups = physicalGroups.filter(group => {
          return group.animals.some(animal => animal.id === livestockId);
        });

        usedGroups = [...usedGroups, ...filteredGroups];
      });

      if (usedGroups.length > 0) {
        setUsedGroups(usedGroups);
        if (selectedLivestockIds.length > 1) {
          setUsedGroupMessage(messages.LIVESTOCKS_TO_PHYS_GROUP_ASSIGNMENT);
        } else {
          setUsedGroupMessage(messages.LIVESTOCK_TO_PHYS_GROUP_ASSIGNMENT(usedGroups[0].name));
        }

        openConfirmPhysGroupModal();
        return;
      }
    }

    // Animal can't be added to a group that doesn't have geofence
    if (selectedLivestockIds?.length) {
      await dispatch(addAnimalsToGroup({ id: groupId, data: { animal_ids: selectedLivestockIds } })).unwrap();
    }

    onUpdateLivestock();

    setIsAddConfirmationOpen(false);
    successToastHandler(messages.UPDATED);
  };

  const actions = useMemo(() => {
    const availableActions = [];

    // 'Clear filters' is available for all users
    availableActions.push({
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!Object.values(selectedFilters)?.length}
          onClick={clearFilters}>
          Clear all filters
        </Button>
      )
    });

    // Animal can't be added to collection
    if (
      hasPermission([PERMISSIONS.LABELS.UPDATE_OWN], user.permissions) &&
      mainGroupType !== MAIN_GROUP_TYPE.COLLECTION
    ) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!selectedLivestockIds?.length}
            onClick={addLivestockToGroup}>
            Add to the group
          </Button>
        )
      });
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!selectedLivestockIds?.length}
            onClick={removeLivestockFromGroup}>
            Remove from group
          </Button>
        )
      });
    }
    return availableActions;
  }, [user.permissions, selectedFilters, selectedLivestockIds]);

  const columns = useGroupLivestockColumns({ filteredInfo, filterOptions });

  const handleAddAnimalsInUsedGroups = useCallback(async () => {
    // remove the animals from group
    try {
      await Promise.all(
        usedGroups.map(group =>
          dispatch(removeAnimalsFromGroup({ id: group.id, data: { animal_ids: selectedLivestockIds } })).unwrap()
        )
      );
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_UPDATE_DATA);
    }

    try {
      await dispatch(addAnimalsToGroup({ id: groupId, data: { animal_ids: selectedLivestockIds } })).unwrap();
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_UPDATE_DATA);
    }

    onUpdateLivestock();

    closeConfirmPhysGroupModal();
    successToastHandler(messages.UPDATED);
  }, [onUpdateLivestock, usedGroups, selectedLivestockIds]);

  // the data itself is mutated, but we need to trigger the changes in component
  const rollBackChanges = useCallback(() => {
    console.log('on rollback');
    setUpdatedDataSource([...data]);
  }, [data])

  return (
    <>
      <Card>
        <AppTable
          headerClass="py-2"
          title="Included/Excluded Livestock"
          actions={actions}
          baseColumns={columns}
          dataSource={dataSource}
          loading={loading}
          tableConfigKey={TABLE_CONFIG_KEYS.INCLUDED_EXCLUDED_LIVESTOCK}
          handleOnChange={handleTableChange}
          updateTableData={handleChangeTableData}
          handleSearchChange={handleSearchChange}
          handleOnSelectRecords={setSelectedLivestockIds}
          updatedDataSource={updatedDataSource}
        />
      </Card>

      <AppModal
        isOpen={isReassignConfirmationOpen}
        confirmButtonText="Yes"
        title="Reassign Livestock"
        handleCancel={() => {
          closeReassignConfirmationModal();
          rollBackChanges();
        }}
        handleConfirm={handleAddAnimalsToGroup}>
        <div className="py-4">{ANIMAL_ERRORS.REASSIGN_LIVESTOCK(animalPhysicalGroup)}</div>
      </AppModal>

      <AppModal
        isOpen={isRemoveConfirmationOpen}
        confirmButtonColor="danger"
        confirmButtonText="Yes"
        title="Remove from the group"
        handleCancel={() => {
          toggleRemoveConfirmModal();
          rollBackChanges();
        }}
        handleConfirm={handleRemoveFromGroup}>
        <div className="py-4">Are you sure about removing the selected animals from the group?</div>
      </AppModal>

      <AppModal
        isOpen={isAddConfirmationOpen}
        confirmButtonText="Yes"
        handleCancel={() => {
          toggleAddConfirmModal();
          rollBackChanges();
        }}
        handleConfirm={handleAddAnimalsToGroup}>
        <div className="py-4">{ANIMAL_ERRORS.UPDATE_GEOFENCE_TO_GROUP}</div>
      </AppModal>

      <AppModal
        isOpen={isAnimalInPhysGroup}
        confirmButtonText="Yes"
        title="Update the group"
        handleCancel={() => {
          closeConfirmPhysGroupModal();
          rollBackChanges();
        }}
        handleConfirm={handleAddAnimalsInUsedGroups}>
        <div className="py-4">{usedGroupMessage}</div>
      </AppModal>
    </>
  );
}

export default memo(GroupLivestock);
